import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack,
  EditOutlined,
  VpnKeyOutlined,
  BlockOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  FiberManualRecord,
  ContentCopyOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';
import { roleColors, roleAvatarColors } from '../../utils/roleColors';
import { getInitials } from '../../utils/getInitials';

interface UserDetail {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  authMethod: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  workshopsCreated: number;
  sessionsEnrolled: number;
  helpRequestsMade: number;
  recentActivity: Array<{ type: string; description: string; timestamp: string }>;
}

const AdminUserDetail: React.FC = () => {
  const { showNotification } = useNotification();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', role: '' });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetMode, setResetMode] = useState<'random' | 'specific'>('random');
  const [specificPassword, setSpecificPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const [passwordResult, setPasswordResult] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [statusLoading, setStatusLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await adminService.getUser(id);
      setUser(res.data);
    } catch {
      setError('Failed to load user');
      showNotification('Failed to load user details', 'error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;
  }
  if (error || !user) {
    return <Alert severity="error">{error || 'User not found'}</Alert>;
  }

  const isSelf = user.id === currentUser?.id;
  const initials = getInitials(user.firstName, user.lastName);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEdit = async () => {
    setEditError('');
    setEditLoading(true);
    try {
      await adminService.updateUser(user.id, editForm);
      setEditOpen(false);
      fetchUser();
    } catch (err: any) {
      setEditError(err.response?.data?.message || 'Failed to update');
    } finally {
      setEditLoading(false);
    }
  };

  const handleResetPw = async () => {
    setResetLoading(true);
    try {
      const data = resetMode === 'specific' ? { password: specificPassword } : undefined;
      const res = await adminService.resetUserPassword(user.id, data);
      setResetPwOpen(false);
      setPasswordResult(res.data.tempPassword);
    } catch {
      showNotification('Failed to reset password', 'error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    setStatusLoading(true);
    try {
      await adminService.updateUserStatus(user.id, { isActive: !user.isActive });
      fetchUser();
    } catch {
      showNotification('Failed to update user status', 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await adminService.deleteUser(user.id);
      navigate('/admin/users');
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      {/* Back button */}
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/users')} sx={{ mb: 2, textTransform: 'none' }}>
        Back to Users
      </Button>

      <Grid container spacing={3}>
        {/* Left column: User profile card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, borderRadius: '12px', textAlign: 'center' }} elevation={0}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: roleAvatarColors[user.role] || '#999',
                fontSize: '1.8rem',
                fontWeight: 600,
                mx: 'auto',
                mb: 2,
              }}
            >
              {initials}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {user.email}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Chip label={user.role} size="small" color={roleColors[user.role] || 'default'} sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
              <Chip
                icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: user.isActive ? '#22c55e' : '#ef4444' }} />}
                label={user.isActive ? 'Active' : 'Inactive'}
                size="small"
                variant="outlined"
              />
            </Box>

            <Box sx={{ textAlign: 'left', mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Member since</Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>{dayjs(user.createdAt).format('MMM D, YYYY')}</Typography>
              <Typography variant="caption" color="text.secondary">Last login</Typography>
              <Typography variant="body2">{user.lastLoginAt ? dayjs(user.lastLoginAt).fromNow() : 'Never'}</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlined />}
                fullWidth
                onClick={() => {
                  setEditForm({ firstName: user.firstName, lastName: user.lastName, email: user.email, role: user.role });
                  setEditError('');
                  setEditOpen(true);
                }}
              >
                Edit
              </Button>
              <Button size="small" variant="outlined" startIcon={<VpnKeyOutlined />} fullWidth onClick={() => { setResetMode('random'); setSpecificPassword(''); setResetPwOpen(true); }}>
                Reset Password
              </Button>
              {!isSelf && (
                <Button
                  size="small"
                  variant="outlined"
                  color={user.isActive ? 'warning' : 'success'}
                  startIcon={user.isActive ? <BlockOutlined /> : <CheckCircleOutlined />}
                  fullWidth
                  onClick={handleStatusToggle}
                  disabled={statusLoading}
                >
                  {user.isActive ? 'Deactivate' : 'Reactivate'}
                </Button>
              )}
              {!isSelf && (
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlined />} fullWidth onClick={() => { setDeleteConfirm(''); setDeleteError(''); setDeleteOpen(true); }}>
                  Delete
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right column: Stats + Activity */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Stats */}
          <Paper sx={{ p: 3, borderRadius: '12px', mb: 3 }} elevation={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Statistics</Typography>
            <Grid container spacing={2}>
              {user.role === 'instructor' && (
                <Grid size={{ xs: 4 }}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: '8px' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>{user.workshopsCreated}</Typography>
                    <Typography variant="caption" color="text.secondary">Tech Evals Created</Typography>
                  </Box>
                </Grid>
              )}
              {user.role === 'student' && (
                <>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: '8px' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6' }}>{user.sessionsEnrolled}</Typography>
                      <Typography variant="caption" color="text.secondary">Sessions Enrolled</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: '8px' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>{user.helpRequestsMade}</Typography>
                      <Typography variant="caption" color="text.secondary">Help Requests</Typography>
                    </Box>
                  </Grid>
                </>
              )}
              {user.role === 'admin' && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">Admin users manage the platform. No student/instructor-specific stats.</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Recent Activity */}
          <Paper sx={{ p: 3, borderRadius: '12px' }} elevation={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Recent Activity</Typography>
            {user.recentActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No recent activity</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {user.recentActivity.map((a, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FiberManualRecord sx={{ fontSize: 8, color: roleAvatarColors[user.role] || '#999' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{a.description}</Typography>
                      <Typography variant="caption" color="text.secondary">{dayjs(a.timestamp).fromNow()}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ---- EDIT DIALOG ---- */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit User</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {editError && <Alert severity="error">{editError}</Alert>}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="First Name" fullWidth size="small" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
            <TextField label="Last Name" fullWidth size="small" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
          </Box>
          <TextField label="Email" fullWidth size="small" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
          <FormControl size="small" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={editForm.role} label="Role" onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
              <MenuItem value="student">Student</MenuItem>
              <MenuItem value="instructor">Instructor</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {editForm.role !== user.role && (
            <Alert severity="warning">Changing role from <strong>{user.role}</strong> to <strong>{editForm.role}</strong></Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editLoading}>{editLoading ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ---- RESET PASSWORD DIALOG ---- */}
      <Dialog open={resetPwOpen} onClose={() => setResetPwOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reset password for <strong>{user.firstName} {user.lastName}</strong>
          </Typography>
          <RadioGroup value={resetMode} onChange={(e) => setResetMode(e.target.value as 'random' | 'specific')}>
            <FormControlLabel value="random" control={<Radio size="small" />} label="Generate random password" />
            <FormControlLabel value="specific" control={<Radio size="small" />} label="Set specific password" />
          </RadioGroup>
          {resetMode === 'specific' && (
            <TextField label="New Password" fullWidth size="small" sx={{ mt: 1.5 }} value={specificPassword} onChange={(e) => setSpecificPassword(e.target.value)} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setResetPwOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPw} disabled={resetLoading || (resetMode === 'specific' && !specificPassword)}>
            {resetLoading ? <CircularProgress size={20} /> : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- PASSWORD RESULT ---- */}
      <Dialog open={!!passwordResult} onClose={() => setPasswordResult(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Password Reset</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>This is the only time this password will be shown.</Alert>
          <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '8px', bgcolor: 'background.default' }}>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>{passwordResult}</Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton onClick={() => copyToClipboard(passwordResult || '')}><ContentCopyOutlined /></IconButton>
            </Tooltip>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={() => setPasswordResult(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ---- DELETE DIALOG ---- */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>Delete User</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Permanently remove <strong>{user.firstName} {user.lastName}</strong>? This cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Type <strong>{user.email}</strong> to confirm:
          </Typography>
          <TextField fullWidth size="small" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder={user.email} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteLoading || deleteConfirm !== user.email}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserDetail;
